/**
 * An in-memory store for loader data, observed by the screens that render
 * it (useQuery). Revisiting a screen (back, a tab tap) renders the stored
 * copy on the first frame instead of a skeleton, and a stored value that
 * changes (a refetch, a save) re-renders the screens showing it.
 *
 * Two kinds of query share the store:
 * - cachedQuery: public data that moves slowly (author profiles, programme
 *   previews, community lists, VictoriaMetrics stats). Fresh within `ttl`:
 *   no request. Stale but within `maxAge`: shown, refreshed in the
 *   background. Missing or past `maxAge`: fetched.
 * - revalidatedQuery: the signed-in user's own data (programmes, schedule,
 *   history, requests, profile). The stored copy shows at once and is
 *   always refetched. Keys are scoped to the user (ownKey).
 *
 * Every entry names what it is built from (`tags`). Writes invalidate by
 * tag at the network layer (cacheInvalidation.ts): an entry no screen shows
 * is dropped; one on screen stays up and refetches, so a change made in
 * this app shows without call sites remembering anything. Other people's
 * changes show within `ttl`.
 *
 * Failures are never stored. Ownership and payment authority never go
 * through the store.
 */

/** What cached data is built from; writes invalidate by these. */
export type CacheTag =
    /** Community programme previews (lists and details). */
    | 'previews'
    /** Author records embedded in previews, coach cards and profiles. */
    | 'authors'
    /** Coach listings and coach attributes (prices, settings). */
    | 'coaches'
    /** VictoriaMetrics aggregates (completions, ratings, requests). */
    | 'stats'
    /** The signed-in user's own data (revalidatedQuery). */
    | 'own'

/**
 * A loader's handle on one stored value. Render it with useQuery (or
 * <QueryView>): the stored value when there is one, else `initial`, else
 * the request. `promise` settles with the request (or the value already
 * known), for code that needs the data outside rendering.
 */
export type Query<T> = {
    /** The store key; null for a fixed value. */
    readonly key: string | null
    readonly promise: Promise<T>
    /** The value known when the handle was made. */
    readonly initial?: { value: T }
    /**
     * The stored entry this query would not serve (past its age, or
     * invalidated): never rendered, even while it is still in the store.
     */
    readonly rejected?: object
}

type Entry = {
    value: unknown
    at: number
    tags: readonly CacheTag[]
    /** Invalidated while on screen: shown until its refetch lands. */
    stale?: true
}
type Inflight = { request: Promise<unknown>; tags: readonly CacheTag[] }

const entries = new Map<string, Entry>()
const inflight = new Map<string, Inflight>()
/** How to fetch each key again, for refetching what a screen shows. */
const refetchers = new Map<string, () => void>()
const listeners = new Map<string, Set<() => void>>()

/**
 * Invalidation counters per tag (and one for clearing everything). A
 * request that started before one of its own tags was invalidated may
 * carry pre-write data, so its result is returned but not stored; requests
 * for other tags are unaffected.
 */
const tagGenerations = new Map<CacheTag, number>()
let clearGeneration = 0

/**
 * Per key, the number of the latest request started (or value written)
 * for it. Responses can land out of order, so only the latest may store:
 * an older response never replaces newer data (a save the screen made).
 */
const writeSeq = new Map<string, number>()

function nextWrite(key: string): number {
    const seq = (writeSeq.get(key) ?? 0) + 1
    writeSeq.set(key, seq)
    return seq
}

function snapshot(tags: readonly CacheTag[]): string {
    return (
        clearGeneration +
        ':' +
        tags.map((tag) => tagGenerations.get(tag) ?? 0).join(',')
    )
}

function notify(key: string): void {
    listeners.get(key)?.forEach((listener) => listener())
}

function watched(key: string): boolean {
    return (listeners.get(key)?.size ?? 0) > 0
}

/** Calls `listener` whenever the value stored for `key` changes. */
export function subscribeQuery(key: string, listener: () => void): () => void {
    let set = listeners.get(key)
    if (!set) listeners.set(key, (set = new Set()))
    set.add(listener)
    return () => {
        set.delete(listener)
        // Only its own set: another subscriber may hold the key by now.
        if (set.size === 0 && listeners.get(key) === set) listeners.delete(key)
    }
}

/** The stored value for `key` (the same object until it changes). */
export function readQuery(key: string): { value: unknown } | undefined {
    return entries.get(key)
}

export const MINUTE = 60_000

/** Own data younger than this is not refetched on a revisit. */
const REVALIDATE_AFTER_MS = 5_000

/**
 * Entries kept at most; the least recently used go first, never one on
 * screen. Lists hold every match of a ranked filter, so without a bound a
 * long session keeps every filter it ever opened.
 */
const MAX_ENTRIES = 200

/** Marks an entry read, so eviction drops the least recently used. */
function touch(key: string, entry: Entry): void {
    entries.delete(key)
    entries.set(key, entry)
}

function store(key: string, entry: Entry): void {
    entries.delete(key)
    entries.set(key, entry)
    notify(key)
    if (entries.size <= MAX_ENTRIES) return
    for (const oldest of entries.keys()) {
        if (entries.size <= MAX_ENTRIES) break
        if (watched(oldest)) continue
        entries.delete(oldest)
        refetchers.delete(oldest)
    }
}

type Options<T> = {
    ttl: number
    maxAge?: number
    tags: readonly CacheTag[]
    /** Values to leave uncached, e.g. a result with nothing in it. */
    keep?: (value: T) => boolean
    /**
     * What a failure resolves to, for data that only decorates a screen:
     * logged, returned, never cached, so the next visit tries again.
     */
    fallback?: (error: unknown) => T
    /**
     * Other entries this response also answers, e.g. each list row is its
     * details page's record, so opening it renders at once. Written only
     * together with this entry, under its tags: a response that an
     * invalidation overtook seeds nothing, so a pre-write row can never
     * come back as a fresh details record. An existing entry is kept.
     */
    seeds?: (value: T) => Iterable<[key: string, value: unknown]>
}

type FetchOptions<T> = Pick<Options<T>, 'tags' | 'keep' | 'seeds' | 'fallback'>

function fetchInto<T>(
    key: string,
    fetcher: () => Promise<T>,
    { tags, keep, seeds, fallback }: FetchOptions<T>
): Promise<T> {
    const pending = inflight.get(key)
    if (pending) return pending.request as Promise<T>
    const startedAt = snapshot(tags)
    const seq = nextWrite(key)
    const request: Promise<T> = fetcher()
        .then(
            (value) => {
                const current =
                    snapshot(tags) === startedAt && writeSeq.get(key) === seq
                if (current && (!keep || keep(value))) {
                    const at = Date.now()
                    for (const [seedKey, seedValue] of seeds?.(value) ?? []) {
                        if (!entries.has(seedKey)) {
                            store(seedKey, { value: seedValue, at, tags })
                        }
                    }
                    // Last, so the entry itself is the newest of its batch.
                    store(key, { value, at, tags })
                }
                return value
            },
            fallback &&
                ((error: unknown) => {
                    console.error(
                        'Query failed, using its fallback:',
                        key,
                        error
                    )
                    return fallback(error)
                })
        )
        .finally(() => {
            if (inflight.get(key)?.request === request) inflight.delete(key)
        })
    inflight.set(key, { request, tags })
    return request
}

/** Logs a background request's failure: a stored copy stays on screen. */
function quiet<T>(key: string, request: Promise<T>): Promise<T> {
    request.catch((error) =>
        console.error('Background refresh failed:', key, error)
    )
    return request
}

/**
 * What a new handle for `key` starts from: it registers how to refetch the
 * key (for invalidations while it is on screen) and reads the stored copy.
 * `initial` is that copy when it may be shown (not invalidated), with its
 * age; the policy deciding whether it is served is the caller's.
 */
function open<T>(
    key: string,
    load: () => Promise<T>
): {
    load: () => Promise<T>
    entry: Entry | undefined
    initial?: { value: T }
    age: number
} {
    refetchers.set(key, () => void quiet(key, load()))
    const entry = entries.get(key)
    if (!entry) return { load, entry, age: Infinity }
    const age = Date.now() - entry.at
    if (entry.stale) return { load, entry, age }
    touch(key, entry)
    return { load, entry, initial: { value: entry.value as T }, age }
}

/** A handle on a stored copy served as is: no request. */
function served<T>(key: string, initial: { value: T }): Query<T> {
    return { key, promise: Promise.resolve(initial.value), initial }
}

export function cachedQuery<T>(
    key: string,
    fetcher: () => Promise<T>,
    options: Options<T>
): Query<T> {
    const { ttl, maxAge = ttl * 10 } = options
    const { load, entry, initial, age } = open(key, () =>
        fetchInto(key, fetcher, options)
    )
    if (initial && age < ttl) return served(key, initial)
    if (initial && age < maxAge) {
        return { key, promise: quiet(key, load()), initial }
    }
    // An entry still stored but past maxAge (or ttl 0) is not served.
    return { key, promise: load(), rejected: entry }
}

/**
 * The user's own data: the stored copy at once (when there is one) and
 * always a new request, which replaces it on screen when it lands.
 */
export function revalidatedQuery<T>(
    key: string,
    fetcher: () => Promise<T>,
    tags: readonly CacheTag[] = ['own']
): Query<T> {
    const { load, entry, initial, age } = open(key, () =>
        fetchInto(key, fetcher, { tags })
    )
    // Just fetched (bouncing between screens): that copy is the fresh one.
    // A write drops or refetches the entry, so this never hides a change.
    if (initial && age < REVALIDATE_AFTER_MS) return served(key, initial)
    // Not joining a request in flight: a revisit wants data from now.
    inflight.delete(key)
    if (initial) return { key, promise: quiet(key, load()), initial }
    // An invalidated copy still shown elsewhere is not served here.
    return { key, promise: load(), rejected: entry }
}

/** A request that is never stored (ownership, payment authority). */
export function uncachedQuery<T>(promise: Promise<T>): Query<T> {
    promise.catch(() => undefined)
    return { key: null, promise }
}

/** A fixed value in the query shape (signed out, feature off). */
export function fixedQuery<T>(value: T): Query<T> {
    return { key: null, promise: Promise.resolve(value), initial: { value } }
}

/**
 * Replaces an entry with data the app already holds, e.g. the merged
 * document a save returned: newer than any request still out for it, and
 * shown at once on every screen observing it.
 */
export function setQueryData<T>(
    key: string,
    value: T,
    tags: readonly CacheTag[] = ['own']
): void {
    nextWrite(key)
    store(key, { value, at: Date.now(), tags })
}

/**
 * Drops one entry, and keeps any request still out for it from storing:
 * for data the app changes without a network write (local drafts).
 */
export function forgetQuery(key: string): void {
    nextWrite(key)
    inflight.delete(key)
    entries.delete(key)
    notify(key)
}

/**
 * Drops every entry built from any of `tags`. One a screen shows stays up,
 * marked stale, and refetches.
 */
export function invalidateTags(tags: readonly CacheTag[]): void {
    if (tags.length === 0) return
    const hit = (own: readonly CacheTag[]) =>
        own.some((tag) => tags.includes(tag))
    for (const tag of tags) {
        tagGenerations.set(tag, (tagGenerations.get(tag) ?? 0) + 1)
    }
    // Requests in flight for these tags may answer with pre-write data:
    // the next caller starts a fresh one instead of joining them.
    for (const [key, pending] of inflight) {
        if (hit(pending.tags)) inflight.delete(key)
    }
    const refetch: (() => void)[] = []
    for (const [key, entry] of entries) {
        if (!hit(entry.tags)) continue
        const again = refetchers.get(key)
        if (watched(key) && again) {
            entries.set(key, { ...entry, stale: true })
            refetch.push(again)
        } else {
            entries.delete(key)
            refetchers.delete(key)
        }
    }
    refetch.forEach((again) => again())
}

/** Drops everything (sign-in changed, tests). */
export function clearQueryCache(): void {
    clearGeneration++
    inflight.clear()
    writeSeq.clear()
    refetchers.clear()
    const keys = [...entries.keys()]
    entries.clear()
    keys.forEach(notify)
}

/** The stored value for `key`, without fetching. */
export function peekQuery<T>(key: string): T | undefined {
    return entries.get(key)?.value as T | undefined
}
